// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Orders / Re-order  (pure helper for pages/OrderReorder.tsx)
// Turns a past order's lines into what could be ordered TODAY: the catch (must still be on the menu), the cut (the same
// one if it is still offered, else the catch's default — flagged), the price paid then vs the price now, and the
// quantity. Prices always come from the menu (data/seafood, packs, cuts), exactly as the cart prices them.
// ─────────────────────────────────────────────────────────────────────────────
import { cutsFor, defaultCut, prepFee, type CutOption } from "../data/cuts";
import { packById, packPrice, type PackSize } from "../data/packs";
import { seafoodItems, type SeafoodItem } from "../data/seafood";
import type { Order, OrderLine } from "./orders";

export interface ReorderEntry {
  line: OrderLine;
  item: SeafoodItem | null;
  pack: PackSize;
  /** The cut that would be used; null when the catch is gone. */
  cut: CutOption | null;
  /** The old cut isn't offered any more, so `cut` is the catch's default. */
  cutChanged: boolean;
  /** Price of one pack today incl. the cut's prep fee (0 when unavailable). */
  todayUnit: number;
  /** What one pack cost when it was ordered. */
  paidUnit: number;
}

export function reorderEntries(order: Order): ReorderEntry[] {
  return order.items.map((line) => {
    const item = seafoodItems.find((candidate) => candidate.id === line.id) ?? null;
    const pack = packById(line.pack);
    if (!item) return { line, item: null, pack, cut: null, cutChanged: false, todayUnit: 0, paidUnit: line.unitPrice };
    const same = cutsFor(item).find((option) => option.name === line.cut);
    const cut = same ?? defaultCut(item);
    return { line, item, pack, cut, cutChanged: !same, todayUnit: packPrice(item, pack) + prepFee(cut, pack), paidUnit: line.unitPrice };
  });
}
